import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DebtSummaryDto } from "../../shared/dtos/debt/DebtSummaryDto";
import { DebtResponseDto } from "../../shared/dtos/debt/DebtResponseDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface DebtState {
  debts: DebtSummaryDto[];
  debt: DebtResponseDto | null;
  pagination: Partial<PaginationDto>;

  loadingFetchAll: boolean;
  loadingFetchOne: boolean;
  loadingPayment: boolean;

  errorFetchAll: string | null;
  errorFetchOne: string | null;
  errorPayment: string | null;

  serverStatus: ServerStatusPayload | null;
}

const initialState: DebtState = {
  debts: [],
  debt: null,
  pagination: {},

  loadingFetchAll: false,
  loadingFetchOne: false,
  loadingPayment: false,

  errorFetchAll: null,
  errorFetchOne: null,
  errorPayment: null,

  serverStatus: null,
};

export const debtSlice = createSlice({
  name: "debt",
  initialState,
  reducers: {
    setLoadingFetchAll(state) {
      state.loadingFetchAll = true;
      state.errorFetchAll = null;
    },

    setLoadingFetchOne(state) {
      state.loadingFetchOne = true;
      state.errorFetchOne = null;
    },

    setLoadingPayment(state) {
      state.loadingPayment = true;
      state.errorPayment = null;
    },

    setErrorFetchAll(state, action: PayloadAction<string | null>) {
      state.loadingFetchAll = false;
      state.errorFetchAll = action.payload;
    },

    setErrorFetchOne(state, action: PayloadAction<string | null>) {
      state.loadingFetchOne = false;
      state.errorFetchOne = action.payload;
    },

    setErrorPayment(state, action: PayloadAction<string | null>) {
      state.loadingPayment = false;
      state.errorPayment = action.payload;
    },

    setDebts(state, action: PayloadAction<DebtSummaryDto[]>) {
      state.loadingFetchAll = false;
      state.debts = action.payload;
    },

    setDebt(state, action: PayloadAction<DebtResponseDto | null>) {
      state.loadingFetchOne = false;
      state.debt = action.payload;
    },

    setPagination(state, action: PayloadAction<Partial<PaginationDto>>) {
      state.pagination = action.payload;
    },

    recordPaymentSuccess(state, action: PayloadAction<DebtResponseDto>) {
      state.loadingPayment = false;
      state.debt = action.payload;

      state.debts = state.debts.map((d) =>
        d.id === action.payload.id
          ? {
              ...d,
              totalPaid: action.payload.totalPayments,
              balance: action.payload.pendingAmount,
            }
          : d,
      );
    },

    setServerStatus(state, action: PayloadAction<ServerStatusPayload | null>) {
      state.serverStatus = action.payload;
    },
  },
});

export const debtActions = debtSlice.actions;

export default debtSlice.reducer;
